/**
 * Login Page Component - Microsoft Entra ID SSO Theme
 * Auto-redirects to SSO - this page is mainly a fallback for error scenarios
 */
import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import headerBg from '../assets/header-bg.svg';
import logo from '../assets/logo.svg';
import { useAuth } from '../contexts/AuthContext';

const LoginPage: React.FC = () => {
  const { login, isLoading, error, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoLoginInitiated = useRef(false);

  // Automatically initiate SSO login when page loads
  useEffect(() => {
    const hasOAuthCallbackParams =
      searchParams.has('code') ||
      searchParams.has('state') ||
      searchParams.has('error');

    if (hasOAuthCallbackParams) {
      navigate(`/auth/callback${globalThis.location.search}`, { replace: true });
      return;
    }

    if (isAuthenticated) {
      // Already authenticated, redirect to home
      navigate('/', { replace: true });
    } else if (!isLoading && !error && !autoLoginInitiated.current) {
      // Not authenticated and no error - automatically start SSO flow
      autoLoginInitiated.current = true;
      login();
    }
  }, [isAuthenticated, isLoading, error, login, navigate, searchParams]);

  // Show loading state during auto-redirect
  if (isLoading || (!error && !isAuthenticated)) {
    return (
      <div className="min-h-screen bg-primary flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-base-100 rounded-lg shadow-xl border-4 border-secondary overflow-hidden">
            <div 
              className="w-full text-center border-b-4 border-red-600 flex items-center justify-center"
              style={{ 
                backgroundImage: `url(${headerBg})`,
                backgroundSize: '100% auto',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundColor: '#FFF6D6',
                height: '68px',
                padding: '12px 2rem',
              }}
            >
              <h1 
                className="text-2xl font-bold flex items-center gap-2"
                style={{
                  color: '#ED1C24',
                }}
              >
                <img src={logo} alt="Archie Logo" className="w-8 h-8" />
                Archie
              </h1>
            </div>
            <div className="p-8 text-center">
              <span className="loading loading-spinner loading-lg text-primary"></span>
              <p className="mt-4 text-base-content/70">Redirecting to Shell SSO...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Manual login form - only shown if there's an error
  return (
    <div className="min-h-screen bg-primary flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-base-100 rounded-lg shadow-xl border-4 border-secondary overflow-hidden">
          {/* Shell Logo/Brand with TopBar styling */}
          <div 
            className="w-full text-center border-b-4 border-red-600 flex items-center justify-center"
            style={{ 
              backgroundImage: `url(${headerBg})`,
              backgroundSize: '100% auto',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundColor: '#FFF6D6',
              height: '68px',
              padding: '12px 2rem',
            }}
          >
            <h1 
              className="text-2xl font-bold flex items-center gap-2"
              style={{
                color: '#ED1C24',
              }}
            >
              <img src={logo} alt="Archie Logo" className="w-8 h-8" />
              Archie
            </h1>
          </div>

          {/* Microsoft Entra ID SSO Login */}
          <div className="p-8 space-y-6">
            <div className="text-center space-y-2">
              <h2 className="text-xl font-semibold text-base-content">Welcome to Archie</h2>
              <p className="text-sm text-base-content/70">
                Sign in with your Shell credentials to continue
              </p>
            </div>

            {error && (
              <div className="alert alert-error">
                <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm">{error}</span>
              </div>
            )}

            <button
              onClick={login}
              disabled={isLoading}
              className="w-full inline-flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 gap-2"
            >
              {isLoading ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Signing in...
                </>
              ) : (
                <>
                  Sign in with Shell SSO
                </>
              )}
            </button>

            <div className="text-center text-xs text-base-content/50">
              <p>By signing in, you agree to comply with Shell&apos;s security policies.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
